import { DragDropModule } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subscription, map } from 'rxjs';
import { SharedModule } from '../../../../../../../app.module.shared';
import { MuseumData, MuseumSearchFilter } from '../../../../../../../models';
import { museumSearchAllRequest, museumSearchDataReset, museumSearchRequest } from '../../../../../../../store/hotels/museum/actions-museum';
import { getFoundMuseums } from '../../../../../../../store/hotels/museum/selectors-museum';
import { AppStore } from '../../../../../../../store/store';
import { CountrySelectComponent } from '../../../../../../shared/input-country/input-country.component';
import { LoadingSpinnerComponent } from '../../../../../../shared/loading-spinner/loading-spinner.component';
import { RegionSelectComponent } from '../../../../../../shared/region-select/region-select.component';

@Component({
  selector: 'app-museum-search-dialog',
  standalone: true,
  templateUrl: './museum-search-dialog.component.html',
  styleUrls: ['./museum-search-dialog.component.css'],
  imports: [
    MatTableModule,
    MatPaginatorModule,
    MatTooltipModule,
    MatSortModule,
    MatDialogModule,
    DragDropModule,
    SharedModule,
    RegionSelectComponent, 
    LoadingSpinnerComponent,
    CountrySelectComponent]
})
export class MuseumSearchDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  searchForm!: FormGroup;
  readonly displayedColumns: string[] = ['name', 'region', 'actions'];
  private dataSourceSubject = new BehaviorSubject<MatTableDataSource<MuseumData>>(new MatTableDataSource<MuseumData>());
  dataSource$ = this.dataSourceSubject.asObservable();
  searchSubscription!: Subscription;

  constructor(
    private fb: FormBuilder,
    private store: Store<AppStore>,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<MuseumSearchDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  ngOnInit(): void {
    this.initializeSearchForm();
    this.searchSubscription = this.store.select(getFoundMuseums)
      .pipe(map(foundMuseums => this.generateDataTableSource(foundMuseums)))
      .subscribe(dataSource => this.dataSourceSubject.next(dataSource));
  }

  ngAfterViewInit(): void {
    this.dataSource$.subscribe(dataSource => {
      if (dataSource) {
        dataSource.paginator = this.paginator;
        dataSource.sort = this.sort;
      }
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  onSearch() {
    const searchFilter = this.getSearchFilterFromForm();
    if (this.isEmptySearchFilter(searchFilter)) {
      this.store.dispatch(museumSearchAllRequest());
    } else {
      this.store.dispatch(museumSearchRequest({ searchFilter }));
    }
  }

  onClearFilter() {
    this.searchForm.reset();
    this.store.dispatch(museumSearchDataReset());
  }

  pickMuseum(museum: MuseumData) {

    this.dialogRef.close(museum);

  }

  cancel() {
    this.dialogRef.close();
  }

  private initializeSearchForm() {
    this.searchForm = this.fb.group({
      name: [null],
      region: [null],
      nation: [null],
      evaneosCode: [null]
    });
  }

  private getSearchFilterFromForm(): MuseumSearchFilter {
    const { region, nation, evaneosCode, name } = this.searchForm.value;
    const filter: MuseumSearchFilter = {};
    if (region) {
      filter.region = region;
    }
    if (nation) {
      filter.nation = nation.code;
    }
    if (evaneosCode) {
      filter.evaneosCode = evaneosCode;
    }
    if (name) {
      filter.name = name;
    }
    return filter;
  }

  private generateDataTableSource(foundMuseums: MuseumData[]): MatTableDataSource<MuseumData> {
    const matTableDataSource = new MatTableDataSource<MuseumData>(foundMuseums);
    return matTableDataSource;
  }

  private isEmptySearchFilter(searchFilter: MuseumSearchFilter) {
    return Object.values(searchFilter).every(value => !value);
  }
}